package org.teddapp.project.ui.nav

/**
 * Ce qu'on demande d'ouvrir en changeant de section.
 *
 * Changer de section ne suffit pas quand on vient d'un lien : cliquer un projet
 * depuis une compétence doit ouvrir *ce* projet, pas la liste des projets. L'écran
 * de destination lit l'intention à son ouverture et la consomme aussitôt — elle ne
 * vaut que pour ce passage, et ne doit pas rejouer au suivant.
 *
 * Un objet partagé plutôt qu'un argument de route : l'application n'a pas de
 * bibliothèque de navigation, et deux identifiants ne justifient pas d'en ajouter une.
 */
object NavIntent {
    private var projectId: String? = null
    private var objectiveId: String? = null
    private var skillId: String? = null
    private var mindMapId: String? = null

    /** Demande l'ouverture d'une carte heuristique précise à la prochaine entrée dans Cartes heuristiques. */
    fun openMindMap(id: String) {
        mindMapId = id
    }

    /** Rend la carte demandée, une seule fois. */
    fun consumeMindMap(): String? = mindMapId.also { mindMapId = null }

    private var newMindMap = false

    /** Demande à Cartes heuristiques d'ouvrir la création d'une carte à sa prochaine entrée. */
    fun createMindMap() {
        newMindMap = true
    }

    /** True une seule fois après [createMindMap]. */
    fun consumeCreateMindMap(): Boolean = newMindMap.also { newMindMap = false }

    /** Demande l'ouverture d'une compétence précise à la prochaine entrée dans Compétences. */
    fun openSkill(id: String) {
        skillId = id
    }

    /** Rend la compétence demandée, une seule fois. */
    fun consumeSkill(): String? = skillId.also { skillId = null }

    /** Une course à lancer dès l'entrée dans Focus : la tâche, sa durée, la compétence qui la reçoit. */
    class FocusLaunch(val task: com.tedd.db.Task, val minutes: Int, val skillId: String?)

    private var focusLaunch: FocusLaunch? = null

    /** Demande à Focus de lancer une course sur cette tâche à sa prochaine ouverture. */
    fun launchFocus(task: com.tedd.db.Task, minutes: Int, skillId: String? = null) {
        focusLaunch = FocusLaunch(task, minutes, skillId)
    }

    /** Rend la course demandée, une seule fois. */
    fun consumeFocusLaunch(): FocusLaunch? = focusLaunch.also { focusLaunch = null }

    /** Demande l'ouverture d'un projet précis à la prochaine entrée dans Projets. */
    fun openProject(id: String) {
        projectId = id
    }

    /** Demande l'ouverture d'un objectif précis à la prochaine entrée dans Objectifs. */
    fun openObjective(id: String) {
        objectiveId = id
    }

    /** Rend le projet demandé, une seule fois. */
    fun consumeProject(): String? = projectId.also { projectId = null }

    /** Rend l'objectif demandé, une seule fois. */
    fun consumeObjective(): String? = objectiveId.also { objectiveId = null }
}
